import { adminUtils, capturePosthogEvent } from '@/helpers';
import { Button, Dropdown, DropdownItem, Flex, Icon, type IconProps, Typography, useToast } from '@alpine-iq/ui-library';
import React from 'react';
import { useCampaignFormArchiveStore } from './CampaignArchiveModals';
import { Campaign } from '@/types';
import { useMessageReportStore } from './MessageReportModal';
import { onSelectCoMarketingReport } from '@/pages/coMarketing-report/CoMarketingReportPage';
import { onSelectCampaignReport } from '@/pages/campaign-report/CampaignReportPage';

type CampaignMoreButtonProps = {
	id?: string;
	close?: () => void;
	campaign?: Partial<Campaign>;
};

export default function CampaignMoreButton(props: CampaignMoreButtonProps) {
	const { addToast } = useToast();
	const setOpenArchive = useCampaignFormArchiveStore((state) => state.setOpen);
	const setOpenMessageReport = useMessageReportStore((state) => state.setOpen);

	const campaign = props.campaign;

	const hasPropsID = !!props.id;
	const isEditingForm = !!campaign?.id;

	const hasCampaignID = hasPropsID || isEditingForm;
	const campaignID = hasPropsID ? props.id : campaign?.id;

	type DropdownItemTypes = {
		label: string;
		icon?: IconProps['type'];
		onClick: (close: () => void) => void;
		css?: string;
		shown?: boolean;
	};

	const dropdownItems: DropdownItemTypes[] = React.useMemo(() => {
		return (
			[
				{
					label: 'Message Report',
					onClick: (close: () => void) => {
						close();
						if (!campaignID) return;
						setOpenMessageReport(campaignID);
						capturePosthogEvent('Campaign - Open Message Report', {
							campaignID: campaignID,
						});
					},
					shown: hasCampaignID,
				},
				{
					label: 'View Report',
					onClick: (close: () => void) => {
						close();
						const isCoMarketing = props.campaign?.isCoMarketing;
						if (isCoMarketing) {
							return;
						}
						if (isCoMarketing) {
							// onSelectCoMarketingReport(campaignID, props.templateID);
						} else {
							onSelectCampaignReport(campaignID ?? '');
						}
					},
					shown: hasCampaignID && !props.campaign?.isCoMarketing,
				},
				{
					label: 'Duplicate',
					onClick: (close: () => void) => {
						close();
						capturePosthogEvent('Campaign - Duplicate Campaign', {
							campaignID: campaignID,
						});
						const url = new URL(window.location.href);
						url.searchParams.set('copy', campaignID?.toString() ?? '');
						url.searchParams.set('campaignID', campaignID?.toString() ?? '');
						window.history.pushState({}, '', url.toString());
						window.location.reload();
					},
					shown: hasCampaignID,
				},
				{
					label: campaign?.archived ? 'Unarchive' : 'Archive',
					onClick: (close: () => void) => {
						close();
						if (!campaignID) return;
						setOpenArchive(campaignID, 'archive');
					},
					shown: hasCampaignID && !campaign?.isCoMarketing && !campaign?.isActive,
				},
				{
					label: 'Delete',
					onClick: (close: () => void) => {
						close();
						if (!campaignID) return;
						setOpenArchive(campaignID, 'delete');
					},
					shown: hasCampaignID && adminUtils.canSeeAll(),
				},
			] as DropdownItemTypes[]
		).filter((item) => item.shown !== false);
	}, [hasCampaignID, campaignID, campaign]);

	if (!hasCampaignID) return null;

	if (hasPropsID) {
		return (
			<>
				{dropdownItems.map((item) => (
					<DropdownItem
						key={item.label}
						onClick={() => item.onClick(props.close ?? (() => 0))}
						css={item.css}
					>
						<Flex align="center">
							{item.icon && (
								<Icon
									type={item.icon}
									size="small"
								/>
							)}
							<Typography style={{ color: 'inherit' }}>{item.label}</Typography>
						</Flex>
					</DropdownItem>
				))}
			</>
		);
	}

	return (
		<>
			<div
				css={`
					.dispense-dropdown-menu {
						top: calc(100% + 18px);
						min-width: 200px;
					}
				`}
			>
				<Dropdown
					Trigger={({ toggleDropdown }) => {
						return (
							<Button
								round={true}
								icon="MORE"
								onClick={() => toggleDropdown()}
							/>
						);
					}}
					position="right"
				>
					{({ close }) => (
						<>
							{dropdownItems.map((item) => (
								<DropdownItem
									key={item.label}
									onClick={(e) => {
										e.stopPropagation();
										item.onClick(close);
									}}
									css={item.css}
								>
									<Flex align="center">
										{item.icon && (
											<Icon
												type={item.icon}
												size="small"
											/>
										)}
										<Typography style={{ color: 'inherit' }}>{item.label}</Typography>
									</Flex>
								</DropdownItem>
							))}
						</>
					)}
				</Dropdown>
			</div>
		</>
	);
}
